import { BuilderComponent } from "@builder.io/react";
import { BuilderContent } from "@builder.io/sdk";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { useEffect } from "react";

import { parallel } from "@/utils/promises";
import serverSideTranslations from "@/utils/serverSideTranslations";

import EVENTS from "@/analytics/events";
import { trackPageLoad } from "@/analytics/tracking";

import { init } from "@/builder";
import { BuilderModel } from "@/builder/types";

import DefaultLayout from "@/layouts/default";

import {
    getBuilderBannerForCategory,
    getBuilderMessages,
    getBuilderNavLinks,
} from "@/pages/api/builder/builder";
import { fetchCollectionMetadataV2 } from "@/pages/api/store-directory/collections";
import { getFooterNavigation, getHeaderNavigation } from "@/pages/api/store-directory/navigation";

import ProductCollection from "@/components/product-collection/product-collection";

import { CollectionV2Props } from "@/interfaces/collection-v2-interface";

import { GetServerSideProps } from "@/types/page";
import { PageProps } from "@/types/page";

type ControlHeaderProps = {
    currentCollection: CollectionV2Props;
};

type OwnProps = {
    builderMessages: Array<BuilderContent>;
    builderNavLinks: Array<BuilderContent>;
    builderBanner: BuilderContent | null;
};

type Props = OwnProps & PageProps & ControlHeaderProps;

type Params = {
    slug: string;
};

init(BuilderModel.MESSAGE);
init(BuilderModel.BANNER);

export const getServerSideProps: GetServerSideProps<Props, Params> = async ({
    locale,
    resolvedUrl,
    params,
}) => {
    if (locale === "global") {
        return {
            notFound: true,
        };
    }

    const slug = params.slug;
    const currentCollection = await fetchCollectionMetadataV2(locale, slug);
    if (!currentCollection) {
        return {
            notFound: true,
        };
    }

    const parallelProps = await parallel({
        headerNavigationItems: () => getHeaderNavigation(locale),
        footerNavItems: () => getFooterNavigation(locale),
        builderMessages: () => getBuilderMessages(locale, { url: resolvedUrl }),
        builderNavLinks: () => getBuilderNavLinks(locale),
        builderBanner: () => getBuilderBannerForCategory(locale, slug),
        _nextI18Next: () => serverSideTranslations(locale),
    });

    return {
        props: {
            locale,
            currentCollection,
            footerLocalNavItems: parallelProps.footerNavItems[0],
            footerRegionalNavItems: parallelProps.footerNavItems[1],
            ...parallelProps,
        },
    };
};

const Category = ({
    builderMessages,
    builderNavLinks,
    builderBanner,
    locale,
    headerNavigationItems,
    footerLocalNavItems,
    footerRegionalNavItems,
    currentCollection,
}: Props): React.ReactNode => {
    const router = useRouter();
    const { t } = useTranslation();
    useEffect(() => {
        trackPageLoad(router.asPath, locale, EVENTS.SHOP_DIRECTORY_CATEGORY_LIST_VIEW, {
            categoryName: currentCollection.slug,
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    return (
        <DefaultLayout
            title={t("category.metaTitle", { categoryName: currentCollection.name })}
            description={t("category.metaDescription", {
                categoryName: currentCollection.name,
            })}
            currentLocale={locale}
            headerNavigationItems={headerNavigationItems}
            footerLocalNavItems={footerLocalNavItems}
            footerRegionalNavItems={footerRegionalNavItems}
            builderNavLinks={builderNavLinks}
            builderMessages={builderMessages}
        >
            <BuilderComponent model={BuilderModel.BANNER} content={builderBanner} />
            <ProductCollection currentCollection={currentCollection} />
        </DefaultLayout>
    );
};

export default Category;
